import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  AveragePlayerScore,
  BenchPlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { BoostButton } from '#src/components/card/BoostButton/index.tsx';
import { CardFooter } from '#src/components/card/CardFooter/index.tsx';
import { CardXpProgressBar } from '#src/components/card/levelup/CardXpProgressBar/index.tsx';
import { useSimpleCardXpProgressBar } from '#src/components/card/levelup/useSimpleCardXpProgressBar.ts';
import { BasicBenchExtraWrapper } from '#src/components/searchPlayerTable/BasicBenchExtraWrapper/index.tsx';
import { useIsBoostAffordable } from '#src/hooks/useIsBoostAffordable.tsx';

import type { TokenBoostDetails_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  anyCard: TokenBoostDetails_anyCard;
  onInfoClick?: () => void;
  totalXpNeeded?: number;
  onToggleCard?: (slug: string, options?: { xpNeeded?: number }) => void;
  selected?: boolean;
};

export const TokenBoostDetails = ({
  anyCard,
  onInfoClick,
  onToggleCard,
  totalXpNeeded = 0,
  selected,
}: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();
  const isBoostAffordable = useIsBoostAffordable();

  const { level, percentage } = useSimpleCardXpProgressBar({
    anyCard,
  });

  const modePerSport: Record<Sport, BenchPlayerScore> = {
    [Sport.FOOTBALL]:
      AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE as unknown as BenchPlayerScore,
    [Sport.NBA]: BenchPlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: useMlbSeasonAverageScore
      ? BenchPlayerScore.SEASON_AVERAGE_SCORE
      : (AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE as unknown as BenchPlayerScore),
  };
  const mode = modePerSport[sport];

  const { xpNeeded } = isBoostAffordable(anyCard, totalXpNeeded);

  return (
    <Vertical gap={1.5}>
      <CardFooter
        anyCard={anyCard}
        benchPlayerScore={mode}
        extra={
          <BasicBenchExtraWrapper>
            <CardXpProgressBar
              size="small"
              level={level}
              percentage={percentage}
            />
          </BasicBenchExtraWrapper>
        }
        onClick={onInfoClick}
      />
      <BoostButton
        size="small"
        anyCard={anyCard}
        selected={selected}
        balanceUsed={totalXpNeeded}
        onClick={() =>
          onToggleCard?.(anyCard.slug, {
            xpNeeded,
          })
        }
      />
    </Vertical>
  );
};

TokenBoostDetails.fragments = {
  anyCard: gql`
    fragment TokenBoostDetails_anyCard on AnyCardInterface {
      slug
      rarity: rarityTyped
      xpNeededForNextGrade
      tokenOwner {
        id
        transferType
      }
      ...CardFooter_anyCardInterface
      ...BoostButton_anyCard
      ...useIsBoostAffordable_anyCard
      ...useSimpleCardXpProgressBar_anyCard
    }
    ${CardFooter.fragments.anyCardInterface}
    ${useIsBoostAffordable.fragments.anyCard}
    ${BoostButton.fragments.anyCard}
    ${useSimpleCardXpProgressBar.fragments.anyCard}
  ` as TypedDocumentNode<TokenBoostDetails_anyCard>,
};
